import type { IEnvelope, IHttpClient, IHttpResponse } from './types';

async function toResponse(res: Response): Promise<IHttpResponse> {
    const raw = res.headers.get('Retry-After');
    const retryAfter = raw !== null ? Number(raw) : NaN;

    let body: IEnvelope = {};
    try {
        body = (await res.json()) as IEnvelope;
    } catch {
        body = {}; // non-JSON / empty body — treat as an empty envelope
    }

    return {
        status: res.status,
        retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
        body,
    };
}

/**
 * Default transport — `fetch` with same-site cookies (`credentials: 'include'`),
 * so the session/X-Session auth rides along. The client takes IHttpClient by
 * injection so the state machine is testable without the network.
 *
 * `getExtraHeaders` adds per-request headers (resolved at call time) — used to send
 * the app session as `X-Session` inside the WebView, where cookie auth is absent.
 */
export function createFetchHttpClient(
    getExtraHeaders?: () => Record<string, string>
): IHttpClient {
    const extra = (): Record<string, string> => getExtraHeaders?.() ?? {};

    return {
        post: async (url, jsonBody, headers, keepalive) =>
            toResponse(
                await fetch(url, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        ...headers,
                        ...extra(),
                    },
                    body: JSON.stringify(jsonBody),
                    // `keepalive` lets a fire-and-forget ping survive the navigation
                    // that immediately follows it (same trick as auth-lib/logout-link)
                    ...(keepalive ? { keepalive: true } : {}),
                })
            ),
        get: async (url, headers) =>
            toResponse(
                await fetch(url, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { ...headers, ...extra() },
                })
            ),
    };
}
